import React, { useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import CommonDialog from "../CommonDialog";
import {
  CustomTextField,
  CustomAutoComplete,
} from "assets/styles/globalStyledComponent";
import ErrorText from "app/modules/student/pages/CreateStudent/components/ErrorText";
import { CloudUpload } from "@mui/icons-material";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";
import { selectActiveSession } from "app/modules/schools/selectors";
import { useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import { useQuery } from "react-query";
import { getAllLibraryDistinctValues } from "app/services/schoolReportingServices/library.service";
import TextFieldLabel from "../../TextFieldLabel";
import { ValidateFileSize } from "app/utils/helper";
import { toast } from "react-toastify";

const AddBookDialog = ({
  open = false,
  onClose = () => {},
  edit = false,
  editData = {},
  addBookMutate,
  updateBookMutate,
}) => {
  const selectedSession = useSelector(selectActiveSession);
  // Fetch distinct dropdown values only when dialog is open and session is selected
  const { data: distinctsData, isLoading: dropdownLoading } = useQuery({
    queryKey: ["getAllLibraryDistinctValues", selectedSession],
    queryFn: () => getAllLibraryDistinctValues({ session: selectedSession }),
    enabled: open && !!selectedSession,
    select: (res) => res?.data || {},
  });

  // --- Formik for form handling ---
  const {
    values,
    handleChange,
    setFieldValue,
    touched,
    errors,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      image: editData?.image || "",
      title: editData?.title || "",
      category: editData?.category || "",
      ISBNNumber: editData?.ISBNNumber || "",
      author: editData?.author || "",
      publisher: editData?.publisher || "",
      publishYear: editData?.publishYear || "",
      edition: editData?.edition || "",
      remark: editData?.remark || "",
      totalQuantity: editData?.totalQuantity || "",
      price: editData?.price || "",
      finePerDay: editData?.finePerDay || "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      image: yup.string(),
      title: yup.string().required("Title is required"),
      category: yup.string().required("Category is required"),
      ISBNNumber: yup
        .string()
        .min(13, "ISBN Number must be at least 13 Digits")
        .max(13, "ISBN Number must be at most 13 Digits")
        .required("ISBN Number is required"),
      author: yup.string().required("Author is required"),
      publisher: yup.string().required("Publisher is required"),
      publishYear: yup.number().required("Publish year is required"),
      edition: yup.string().required("Edition is required"),
      remark: yup.string(),
      totalQuantity: yup
        .number()
        .required("Total quantity is required")
        .min(1, "Quantity must be at least 1"),
      price: yup
        .number()
        .required("Price is required")
        .min(1, "Price must be at least 1"),
      finePerDay: yup
        .number()
        .required("Fine per day is required")
        .min(0, "Fine cannot be negative"),
    }),
    onSubmit: (formValues) => {
      if (edit) {
        updateBookMutate({
          ...formValues,
          libraryBookDocId: editData?._id,
        });
      } else {
        const bookData = {
          ...formValues,
          session: selectedSession,
        };
        addBookMutate(bookData);
      }
    },
  });

  // --- Loading state from mutation ---
  // Use mutation loading state for LoadingButton
  const loading = edit
    ? typeof updateBookMutate?.isLoading === "boolean"
      ? updateBookMutate.isLoading
      : false
    : typeof addBookMutate?.isLoading === "boolean"
    ? addBookMutate.isLoading
    : false;

  // --- Handlers ---
  const { handleUploadImage } = useFolderWiseFileUploader();
  const [uploading, setUploading] = useState(false);
  const handleAddBookDialogClose = () => {
    resetForm();
    setUploading(false);
    onClose();
  };
  const handleAddImage = async (e) => {
    const imageFile = e.target.files[0];
    if (!imageFile) return;

    const { valid, message } = ValidateFileSize(imageFile, {
      validateImage: true,
      imageLimit: 0.5 * 1048576, // 0.5 MB
    });

    if (!valid) {
      toast.error(message);
      return;
    }

    setUploading(true);
    let attachmentUrl = await handleUploadImage(
      false,
      e,
      "withSession",
      "LIBRARY",
      "BOOK"
    );
    setFieldValue("image", attachmentUrl?.fileURL);
    setUploading(false);
  };

  return (
    <CommonDialog
      title={edit ? "Edit Book" : "Add Book"}
      open={open}
      onClose={handleAddBookDialogClose}
      minWidth="650px"
      fullWidth
    >
      <DialogContent>
        {/* upload image */}
        <>
          <Box>
            <img
              src={values?.image ? values?.image : "/image-placeholder.jpeg"}
              alt="Book Cover"
              style={{
                height: "250px",
                width: "100%",
                objectFit: "cover",
                borderRadius: "10px",
              }}
            />
            {errors?.image && <ErrorText text={errors?.image} />}
          </Box>
          <Stack direction="row" spacing={2} mt={2}>
            <Button
              variant="contained"
              component="label"
              size="large"
              color="secondary"
              startIcon={<CloudUpload />}
              sx={{ my: 1 }}
              disabled={uploading}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleAddImage}
                hidden
              />
              {uploading ? "Uploading..." : values?.image ? "Change" : "Browse"}
            </Button>
          </Stack>
        </>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={4}>
            <TextFieldLabel title="Title" />
            <CustomTextField
              placeholder="Type title"
              fullWidth
              name="title"
              value={values.title}
              onChange={handleChange}
              error={Boolean(touched.title && errors.title)}
              helperText={touched.title && errors.title}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Category" />
            <CustomAutoComplete
              placeholder="Select or type category"
              options={distinctsData?.distinctCategory || []}
              value={values.category}
              onChange={(val) => setFieldValue("category", val || "")}
              freeSolo
              disabled={dropdownLoading}
              error={touched.category && errors.category}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="ISBN Number" />
            <CustomTextField
              fullWidth
              name="ISBNNumber"
              value={values.ISBNNumber}
              onChange={handleChange}
              error={Boolean(touched.ISBNNumber && errors.ISBNNumber)}
              helperText={touched.ISBNNumber && errors.ISBNNumber}
              placeholder="Type ISBN Number"
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Author" />
            <CustomAutoComplete
              placeholder="Select or type author"
              options={distinctsData?.distinctAuthor || []}
              value={values.author}
              onChange={(val) => setFieldValue("author", val || "")}
              freeSolo
              disabled={dropdownLoading}
              error={touched.author && errors.author}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Publisher" />
            <CustomAutoComplete
              placeholder="Select or type publisher"
              options={distinctsData?.distinctPublisher || []}
              value={values.publisher}
              onChange={(val) => setFieldValue("publisher", val || "")}
              freeSolo
              disabled={dropdownLoading}
              error={touched.publisher && errors.publisher}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Publish Year" />
            <CustomAutoComplete
              placeholder="Select or type publish year"
              options={(distinctsData?.distinctPublishYear || []).map(String)}
              value={values.publishYear}
              onChange={(val) => setFieldValue("publishYear", val || "")}
              freeSolo
              disabled={dropdownLoading}
              error={touched.publishYear && errors.publishYear}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Edition" />
            <CustomAutoComplete
              placeholder="Select or type edition"
              options={distinctsData?.distinctEdition || []}
              value={values.edition}
              onChange={(val) => setFieldValue("edition", val || "")}
              freeSolo
              disabled={dropdownLoading}
              error={touched.edition && errors.edition}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Total Quantity" />
            <CustomTextField
              placeholder="Type total quantity"
              fullWidth
              name="totalQuantity"
              type="number"
              value={values.totalQuantity}
              onChange={handleChange}
              error={Boolean(touched.totalQuantity && errors.totalQuantity)}
              helperText={touched.totalQuantity && errors.totalQuantity}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Price" />
            <CustomTextField
              fullWidth
              placeholder="Type price"
              name="price"
              type="number"
              value={values.price}
              onChange={handleChange}
              error={Boolean(touched.price && errors.price)}
              helperText={touched.price && errors.price}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Fine Per Day" />
            <CustomTextField
              fullWidth
              placeholder="Type fine per day"
              name="finePerDay"
              type="number"
              value={values.finePerDay}
              onChange={handleChange}
              error={Boolean(touched.finePerDay && errors.finePerDay)}
              helperText={touched.finePerDay && errors.finePerDay}
            />
          </Grid>
          <Grid item xs={4}>
            <TextFieldLabel title="Remark" />
            <CustomTextField
              fullWidth
              placeholder="Type remark"
              name="remark"
              value={values.remark}
              onChange={handleChange}
              error={Boolean(touched.remark && errors.remark)}
              helperText={touched.remark && errors.remark}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleAddBookDialogClose}
          color="error"
          variant="outlined"
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={loading || uploading}
        >
          {edit ? "Update" : "Add"}
        </LoadingButton>
      </DialogActions>
    </CommonDialog>
  );
};

export default AddBookDialog;
